// Modern (theme="d") rewrite of the Reminders → Active screen.
//
// Mirrors the structure & sizing of the Claude Design "D" prototype
// (project/Nailzona/screen.jsx). Only renders when ThemeModernContext = true.
// Legacy ActiveReminders stays untouched in Reminders.tsx and continues to
// render when the toggle is off.
//
// Structure (top → bottom):
//   1. Sub-tabs row (.nz-segwrap)
//   2. Pinned create buttons (.nz-create + .nz-create.alt)
//   3. Search bar (.nz-search)
//   4. Summary line (.nz-summary)
//   5. Groups (super_urgent / urgent / later) — each an accordion (.nz-group)
//      with a colored header (.nz-ghead) and a stack of sections inside.
//   6. Each Section (.nz-section-label + cards)
//   7. Each Card — D-style with avatar, urgency tag, round contacts,
//      message bubble, OutcomePicker grid, snooze actions row.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { REFERRAL_SOURCES } from "../referralSources.js";
import { contactedText } from "../contacted.js";
import type { ReactNode } from "react";
import { api } from "../api.js";
import { DateSheet } from "../components/DateSheet.js";
import { ClientDetail } from "./Clients.js";
import { useScrollMemory } from "../scrollMemory.js";
import {
  PeriodPicker,
  periodToIsoRange,
  type PeriodValue,
} from "../components/PeriodPicker.js";
import type {
  ClientLite,
  ReminderRow,
  ReminderKind,
  SectionDef,
} from "./Reminders.js";
import { SECTIONS } from "./Reminders.js";

// ---- Constants mirrored from the design prototype ----

type SectionGroup = "superurgent" | "urgent" | "later";

/** Загальна задача — без клієнтки (окрема таблиця GeneralTask). */
type GeneralTaskRow = {
  id: string;
  title: string;
  body: string | null;
  priority: string;
  status: string;
  dueAt: string | null;
  createdAt: string;
};

/** У яку групу лягає загальна задача — за її власним пріоритетом. */
function groupOfTask(t: GeneralTaskRow): SectionGroup {
  if (t.priority === "super_urgent") return "superurgent";
  if (t.priority === "urgent") return "urgent";
  return "later";
}

const GROUP_META: Record<
  SectionGroup,
  {
    emoji: string;
    title: string;
    hint: string;
    tone: "red" | "amber" | "slate";
    tagText: string;
  }
> = {
  superurgent: {
    emoji: "🚨",
    title: "Супер термінові",
    hint: "Зробити прямо зараз",
    tone: "red",
    tagText: "🚨 терміново",
  },
  urgent: {
    emoji: "🔴",
    title: "Термінові",
    hint: "Сьогодні до кінця дня",
    tone: "amber",
    tagText: "🔴 сьогодні",
  },
  later: {
    emoji: "📋",
    title: "Потім",
    hint: "Як буде час",
    tone: "slate",
    tagText: "📋 потім",
  },
};

const OUTCOMES: {
  v: string;
  label: string;
  emoji: string;
  c: "green" | "red" | "amber" | "blue" | "slate";
}[] = [
  { v: "booked", label: "Записалась", emoji: "✅", c: "green" },
  { v: "declined", label: "Відмовилась", emoji: "❌", c: "red" },
  { v: "not_replied", label: "Не відповіла", emoji: "🤷", c: "slate" },
  { v: "review_mixed", label: "50/50", emoji: "🟡", c: "amber" },
  { v: "review_positive", label: "Гарний відгук", emoji: "👍", c: "blue" },
  { v: "review_negative", label: "Поганий відгук", emoji: "👎", c: "red" },
];

const NEEDS_COMMENT = new Set([
  "declined",
  "review_positive",
  "review_negative",
  "review_mixed",
]);

// ---- Helpers ----

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0] ?? "";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}

function avatarGradient(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(150deg, hsl(${hue} 52% 70%), hsl(${
    (hue + 26) % 360
  } 50% 58%))`;
}

function contactHref(
  kind: "insta" | "viber" | "tg" | "call",
  c: ClientLite,
): string {
  if (kind === "call" && c.phone) return `tel:${c.phone}`;
  if (kind === "viber" && c.phone) {
    const num = c.viberPhone ?? c.phone;
    return `viber://chat?number=${encodeURIComponent(num)}`;
  }
  if (kind === "tg" && c.telegramUsername) {
    return `https://t.me/${c.telegramUsername.replace(/^@/, "")}`;
  }
  if (kind === "insta" && c.instagram) {
    return `https://instagram.com/${c.instagram.replace(/^@/, "")}`;
  }
  return "#";
}

function clientName(c: ClientLite): string {
  return `${c.firstName} ${c.lastName ?? ""}`.trim() || "Без імені";
}

function groupOf(r: ReminderRow): SectionGroup | null {
  if (r.kind === "manual") {
    if (r.manualPriority === "super_urgent") return "superurgent";
    if (r.manualPriority === "urgent") return "urgent";
    return "later";
  }
  const sec = SECTIONS.find((s) => s.kind === r.kind);
  return (sec?.group as SectionGroup | undefined) ?? null;
}

function reminderMeta(r: ReminderRow): string {
  const parts: string[] = [];
  const d = new Date(r.dueAt);
  const ago =
    (Date.now() - new Date(r.createdAt).getTime()) / (24 * 60 * 60 * 1000);
  if (r.kind === "manual" && r.manualTitle) {
    parts.push(r.manualTitle);
  } else {
    const sec = SECTIONS.find((s) => s.kind === r.kind);
    if (sec) parts.push(sec.title);
  }
  if (ago < 1) parts.push("сьогодні");
  else if (ago < 2) parts.push("вчора");
  else parts.push(`${Math.floor(ago)} дн тому`);
  return parts.join(" · ");
}

/** Спеціальний meta-рендер для rebook_1m — об'єднує заголовок типу і
 *  стан (сьогодні / протермінована N дн) в один інлайн-рядок.
 *  Для overdue >= 1 «протермінована» рендериться як червоний фрагмент
 *  усередині того ж рядка — без зайвого бейджа під ним. */
function RebookOneMeta({ r }: { r: { dueAt: string } }) {
  const dayMs = 24 * 60 * 60 * 1000;
  const overdueDays = Math.floor(
    (Date.now() - new Date(r.dueAt).getTime()) / dayMs,
  );
  const sec = SECTIONS.find((s) => s.kind === "rebook_1m");
  const title = sec?.title ?? "Рівно місяць без візиту";
  if (overdueDays < 1) {
    return <>{`${title} · сьогодні`}</>;
  }
  return (
    <>
      {title}
      {" · "}
      <span className="nz-overdue-inline">
        ⏰ протермінована {overdueDays} {overdueDays === 1 ? "день" : "дн"}
      </span>
    </>
  );
}

// Скрипти для ПРОТЕРМІНОВАНИХ rebook_1m (overdue ≥ 1 день). Стандартний
// «сьогодні рівно місяць 🥳» тут брехливий — місяць уже минув. Ці теплі,
// без прив'язки до конкретної дати.
const OVERDUE_REBOOK_SCRIPTS = [
  "Доброго дня, {{firstName}} 🌸\n\n" +
    "З Вашого останнього візиту вже пройшов місяць ✨\n" +
    "Хочемо запропонувати Вам вільні віконця, поки ще є зручні часи для запису\n\n" +
    "Поставте + і я надішлю найближчі вільні слоти для запису",
  "Доброго дня, {{firstName}} 🌸\n" +
    "Вже трохи більше місяця, як ми бачились — нігтики, мабуть, просяться " +
    "на оновлення?) Підкажіть, коли вам зручно — підберу гарне віконце 💛",
  "{{firstName}}, скучили за вами 🥰\n" +
    "Місяць пролетів — саме час освіжити манікюр. Надіслати вільні віконця?)",
  "Доброго дня, {{firstName}} 💅\n" +
    "Помітила, що ваш останній візит був більше місяця тому. Запишемось на " +
    "оновлення? Є гарні віконця найближчими днями ✨",
];

// ---- Components ----

type SubTab = "active" | "snoozed" | "done" | "stats";

export function ActiveRemindersModern({
  isOwner,
  subView,
  onSubViewChange,
}: {
  isOwner: boolean;
  subView: SubTab;
  onSubViewChange: (v: SubTab) => void;
}) {
  const [items, setItems] = useState<ReminderRow[] | null>(null);
  // Загальні задачі (без клієнтки) живуть в окремій таблиці GeneralTask.
  // Новий дизайн їх СТВОРЮВАВ, але ніде не показував — Люба (примусово
  // новий дизайн) додала три задачі, отримала «Задачу збережено ✓», і не
  // бачила жодної. Тепер тягнемо разом з нагадуваннями.
  const [tasks, setTasks] = useState<GeneralTaskRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [openGroups, setOpenGroups] = useState<Record<SectionGroup, boolean>>({
    superurgent: true,
    urgent: true,
    later: false,
  });
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const captureScroll = useScrollMemory(!!selectedClientId);
  const openClient = (id: string) => {
    captureScroll();
    setSelectedClientId(id);
  };
  const [toast, setToast] = useState<string | null>(null);
  const toastT = useRef<number | null>(null);
  const [createForm, setCreateForm] = useState(false);
  const [generalForm, setGeneralForm] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.reminders(), api.tasks()])
      .then(([rems, ts]) => {
        setItems(rems as ReminderRow[]);
        setTasks(ts as GeneralTaskRow[]);
      })
      .catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fireToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastT.current) window.clearTimeout(toastT.current);
    toastT.current = window.setTimeout(() => setToast(null), 1800);
  }, []);

  /**
   * Обробка помилки дії над задачею. Якщо бекенд каже REMINDER_GONE — це не
   * поломка: задачі вже немає (нічний cron о 23:30 перебудовує чергу, або
   * клієнтка перезаписалась і rebook прибрався). Тихо оновлюємо список і
   * показуємо зрозумілий тост замість сирого 500 у alert.
   */
  const handleActionError = useCallback(
    (e: unknown) => {
      const msg = String(e);
      if (msg.includes("REMINDER_GONE")) {
        load();
        fireToast("Задача вже закрита або зникла — список оновлено");
        return;
      }
      alert(msg);
    },
    [load, fireToast],
  );

  const snooze = useCallback(
    async (id: string, days: number) => {
      try {
        await api.snoozeReminder(id, days);
        fireToast(`Відкладено на ${days} дн`);
        load();
      } catch (e) {
        handleActionError(e);
      }
    },
    [load, fireToast, handleActionError],
  );

  const setOutcomes = useCallback(
    async (id: string, tags: string[], note?: string) => {
      try {
        await api.setReminderOutcomes(id, tags, note);
        fireToast("Збережено ✓");
        load();
      } catch (e) {
        handleActionError(e);
      }
    },
    [load, fireToast, handleActionError],
  );

  const skip = useCallback(
    async (id: string) => {
      try {
        await api.skipReminder(id);
        fireToast("Пропущено");
        load();
      } catch (e) {
        handleActionError(e);
      }
    },
    [load, fireToast, handleActionError],
  );

  const ignore = useCallback(
    async (clientId: string) => {
      try {
        await api.setClientDoNotRemind(clientId, true);
        fireToast("Клієнтку ігноровано");
        load();
      } catch (e) {
        handleActionError(e);
      }
    },
    [load, fireToast, handleActionError],
  );

  const filtered = useMemo(() => {
    const all = items ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((r) => {
      const name = clientName(r.client).toLowerCase();
      const ig = (r.client.instagram ?? "").toLowerCase();
      const ph = (r.client.phone ?? "").toLowerCase();
      return name.includes(needle) || ig.includes(needle) || ph.includes(needle);
    });
  }, [items, q]);

  const byGroup = useMemo(() => {
    const out: Record<SectionGroup, ReminderRow[]> = {
      superurgent: [],
      urgent: [],
      later: [],
    };
    for (const r of filtered) {
      const g = groupOf(r);
      if (g) out[g].push(r);
    }
    // Усередині СУПЕР ТЕРМІНОВО: спершу «сьогодні рівно місяць» (овердю=0),
    // потім протерміновані за зростанням овердю-днів — свіжіші протерміновані
    // вище, бо там ще є шанс догнати клієнтку. Овердю рахується від dueAt
    // (= lastVisit + 30d для rebook_1m).
    const dayMs = 24 * 60 * 60 * 1000;
    out.superurgent.sort((a, b) => {
      const overA = Math.floor((Date.now() - new Date(a.dueAt).getTime()) / dayMs);
      const overB = Math.floor((Date.now() - new Date(b.dueAt).getTime()) / dayMs);
      return overA - overB;
    });
    return out;
  }, [filtered]);

  // Загальні задачі — той самий пошук (по заголовку/тексту, бо клієнтки нема)
  // і ті самі три групи за пріоритетом.
  const tasksByGroup = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out: Record<SectionGroup, GeneralTaskRow[]> = {
      superurgent: [],
      urgent: [],
      later: [],
    };
    for (const t of tasks) {
      if (
        needle &&
        !t.title.toLowerCase().includes(needle) &&
        !(t.body ?? "").toLowerCase().includes(needle)
      )
        continue;
      out[groupOfTask(t)].push(t);
    }
    return out;
  }, [tasks, q]);

  if (error) {
    return (
      <div className="card">
        <p>Не вдалось завантажити нагадування.</p>
        <p className="hint">{error}</p>
      </div>
    );
  }
  if (!items) return <div className="hint">Завантаження...</div>;

  // «Усе опрацьовано» має враховувати і загальні задачі — інакше при
  // самих лише загальних задачах екран показував би порожній стан.
  const totalLive =
    filtered.length +
    tasksByGroup.superurgent.length +
    tasksByGroup.urgent.length +
    tasksByGroup.later.length;

  return (
    <>
      {selectedClientId && (
        <ClientDetail
          id={selectedClientId}
          onBack={() => setSelectedClientId(null)}
        />
      )}
      <div style={{ display: selectedClientId ? "none" : "block" }}>
        {/* Sub-tabs are now rendered by the parent RemindersTab — keeps
            the row visible on every sub-view so it doesn't blink away. */}

        {/* Pinned create buttons — owner only, just like the legacy code */}
        {isOwner && (
          <div className="nz-create-row">
            <button
              className="nz-create"
              onClick={() => setCreateForm((s) => !s)}
            >
              <span className="nz-ic">＋</span> Нова задача (ручна)
            </button>
            {createForm && (
              <ManualCreateForm
                onClose={() => setCreateForm(false)}
                onCreated={() => {
                  setCreateForm(false);
                  load();
                  fireToast("Задачу збережено ✓");
                }}
              />
            )}
            <button
              className="nz-create alt"
              onClick={() => setGeneralForm((s) => !s)}
            >
              <span className="nz-ic">＋</span> Загальна задача (без клієнтки)
            </button>
            {generalForm && (
              <GeneralTaskForm
                onClose={() => setGeneralForm(false)}
                onCreated={() => {
                  setGeneralForm(false);
                  load();
                  fireToast("Задачу збережено ✓");
                }}
              />
            )}
          </div>
        )}

        {/* Search */}
        <div className="nz-search">
          <SearchIcon />
          <input
            placeholder="Пошук — ім'я, телефон, інстаграм"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {/* Summary */}
        <div className="nz-summary">
          {q ? (
            <>
              Знайдено: <b>{filtered.length}</b> з {items.length}
            </>
          ) : (
            <>
              Всього <b>{totalLive} задач</b>. Йдемо зверху вниз — спочатку
              найважливіше.
            </>
          )}
        </div>

        {/* Groups */}
        {(["superurgent", "urgent", "later"] as SectionGroup[]).map((g) => {
          const meta = GROUP_META[g];
          const list = byGroup[g];
          const taskList = tasksByGroup[g];
          if (list.length === 0 && taskList.length === 0) return null;
          return (
            <GroupAccordion
              key={g}
              tone={meta.tone}
              emoji={meta.emoji}
              title={meta.title}
              hint={meta.hint}
              count={list.length + taskList.length}
              open={openGroups[g]}
              onToggle={() =>
                setOpenGroups((o) => ({ ...o, [g]: !o[g] }))
              }
            >
              {taskList.length > 0 && (
                <GeneralTasksBlock
                  items={taskList}
                  onRefresh={load}
                  onToast={fireToast}
                  isOwner={isOwner}
                />
              )}
              <GroupBody
                group={g}
                items={list}
                onSnooze={snooze}
                onOutcomes={setOutcomes}
                onSkip={skip}
                onIgnore={ignore}
                onOpenClient={openClient}
                onToast={fireToast}
                onRefresh={load}
                isOwner={isOwner}
              />
            </GroupAccordion>
          );
        })}

        {totalLive === 0 && (
          <div className="nz-empty" style={{ marginTop: 32 }}>
            <div className="em">🎉</div>
            <div className="et">Усе опрацьовано!</div>
            <div className="es">Жодної активної задачі. Гарна робота 💖</div>
          </div>
        )}

        <div style={{ height: 16 }} />
      </div>

      {/* Toast */}
      <div className="nz-toast" data-show={!!toast}>
        {toast}
      </div>
    </>
  );
}

// ---- Загальні задачі (без клієнтки) ----
// Створювались і раніше, але новий дизайн їх не показував узагалі —
// тільки старий. Рендеримо всередині тієї ж групи пріоритету, що й
// нагадування, окремим блоком зверху.

function GeneralTasksBlock({
  items,
  onRefresh,
  onToast,
  isOwner,
}: {
  items: GeneralTaskRow[];
  onRefresh: () => void;
  onToast: (m: string) => void;
  isOwner: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function done(id: string) {
    setBusy(id);
    try {
      await api.markTaskDone(id);
      onToast("Задачу закрито ✓");
      onRefresh();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(null);
    }
  }
  async function del(id: string) {
    if (!confirm("Видалити задачу?")) return;
    setBusy(id);
    try {
      await api.deleteTask(id);
      onToast("Задачу видалено");
      onRefresh();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {items.map((t) => (
        <div key={t.id} className="nz-gtask">
          <div className="nz-gtask-head">
            <span className="nz-gtask-badge">🏢 Загальна</span>
            {isOwner && (
              <button
                className="nz-gtask-del"
                onClick={() => del(t.id)}
                disabled={busy === t.id}
                aria-label="Видалити задачу"
              >
                🗑
              </button>
            )}
          </div>
          <div className="nz-gtask-title">{t.title}</div>
          {t.body && <div className="nz-gtask-body">{t.body}</div>}
          {t.dueAt && (
            <div className="nz-gtask-due">
              📅 До{" "}
              {new Date(t.dueAt).toLocaleDateString("uk-UA", {
                day: "2-digit",
                month: "2-digit",
              })}
            </div>
          )}
          <button
            className="nz-gtask-done"
            onClick={() => done(t.id)}
            disabled={busy === t.id}
          >
            ✅ Готово
          </button>
        </div>
      ))}
    </>
  );
}

// ---- Sub-tab pill button ----

function SegBtn({
  label,
  on,
  onClick,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button className="nz-seg" data-on={on} onClick={onClick}>
      {label}
    </button>
  );
}

// ---- Group accordion ----

function GroupAccordion({
  tone,
  emoji,
  title,
  hint,
  count,
  open,
  onToggle,
  children,
}: {
  tone: "red" | "amber" | "slate";
  emoji: string;
  title: string;
  hint: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="nz-group" data-tone={tone} style={{ marginTop: 14 }}>
      <button className="nz-ghead" onClick={onToggle}>
        <div className="nz-gdot">{emoji}</div>
        <div className="nz-gtitle">
          <div className="t">{title}</div>
          <div className="s">{hint}</div>
        </div>
        <div className="nz-gcount">{count}</div>
        <span className="nz-chev" data-open={open}>
          <ChevronRight />
        </span>
      </button>
      <div
        className="nz-gbody"
        data-open={open}
        style={{ maxHeight: open ? 99999 : 0 }}
      >
        {children}
      </div>
    </div>
  );
}

function GroupBody({
  group,
  items,
  onSnooze,
  onOutcomes,
  onSkip,
  onIgnore,
  onOpenClient,
  onToast,
  onRefresh,
  isOwner,
}: {
  group: SectionGroup;
  items: ReminderRow[];
  onSnooze: (id: string, days: number) => void;
  onOutcomes: (id: string, tags: string[], note?: string) => void;
  onSkip: (id: string) => void;
  onIgnore: (clientId: string) => void;
  onOpenClient: (id: string) => void;
  onToast: (msg: string) => void;
  onRefresh: () => void;
  isOwner: boolean;
}) {
  const byKind = new Map<ReminderKind, ReminderRow[]>();
  for (const r of items) {
    const arr = byKind.get(r.kind) ?? [];
    arr.push(r);
    byKind.set(r.kind, arr);
  }
  // Order sections per the SECTIONS array (canonical priority within group),
  // then manual at the end.
  const groupSections: SectionDef[] = SECTIONS.filter(
    (s) => s.group === (group as string),
  );
  const manualList = byKind.get("manual") ?? [];

  return (
    <>
      {groupSections.map((sec) => {
        const arr = byKind.get(sec.kind) ?? [];
        if (arr.length === 0) return null;
        return (
          <SectionBlock
            key={sec.kind}
            emoji={sec.emoji}
            title={sec.title}
            hint={sec.hint}
            count={arr.length}
            cards={arr}
            group={group}
            onSnooze={onSnooze}
            onOutcomes={onOutcomes}
            onSkip={onSkip}
            onIgnore={onIgnore}
            onOpenClient={onOpenClient}
            onToast={onToast}
            onRefresh={onRefresh}
            isOwner={isOwner}
            hasDiscount={sec.hasDiscount}
          />
        );
      })}
      {manualList.length > 0 && (
        <SectionBlock
          emoji="📝"
          title="Ручні задачі"
          hint="Створені власницею"
          count={manualList.length}
          cards={manualList}
          group={group}
          onSnooze={onSnooze}
          onOutcomes={onOutcomes}
          onSkip={onSkip}
          onIgnore={onIgnore}
          onOpenClient={onOpenClient}
          onToast={onToast}
          onRefresh={onRefresh}
          isOwner={isOwner}
          hasDiscount={false}
        />
      )}
    </>
  );
}

function SectionBlock({
  emoji,
  title,
  hint,
  count,
  cards,
  group,
  onSnooze,
  onOutcomes,
  onSkip,
  onIgnore,
  onOpenClient,
  onToast,
  onRefresh,
  isOwner,
  hasDiscount,
}: {
  emoji: string;
  title: string;
  hint: string;
  count: number;
  cards: ReminderRow[];
  group: SectionGroup;
  onSnooze: (id: string, days: number) => void;
  onOutcomes: (id: string, tags: string[], note?: string) => void;
  onSkip: (id: string) => void;
  onIgnore: (clientId: string) => void;
  onOpenClient: (id: string) => void;
  onToast: (msg: string) => void;
  onRefresh: () => void;
  isOwner: boolean;
  hasDiscount: boolean;
}) {
  const [shown, setShown] = useState(8);
  const slice = cards.slice(0, shown);
  return (
    <div>
      <div className="nz-section-label">
        <span style={{ fontSize: 14 }}>{emoji}</span>
        {title}
        <span className="cnt">· {count}</span>
      </div>
      <div className="nz-section-hint">{hint}</div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 11,
          marginTop: 9,
        }}
      >
        {slice.map((r) => (
          <ReminderCardModern
            key={r.id}
            r={r}
            tone={GROUP_META[group].tone}
            tagText={GROUP_META[group].tagText}
            hasDiscount={hasDiscount}
            onSnooze={onSnooze}
            onOutcomes={onOutcomes}
            onSkip={onSkip}
            onIgnore={onIgnore}
            onOpenClient={onOpenClient}
            onToast={onToast}
            onRefresh={onRefresh}
            isOwner={isOwner}
          />
        ))}
        {shown < cards.length && (
          <button
            className="nz-showmore"
            onClick={() => setShown((s) => s + 8)}
          >
            ⬇ Показати ще ({cards.length - shown})
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Reminder card (D-style) ----

const CONTACTS_META = {
  insta: { cls: "insta", glyph: "📷", label: "Instagram" },
  viber: { cls: "viber", glyph: "💬", label: "Viber" },
  tg: { cls: "tg", glyph: "✈️", label: "Telegram" },
  call: { cls: "call", glyph: "📞", label: "Дзвонити" },
} as const;

function ReminderCardModern({
  r,
  tone,
  tagText,
  hasDiscount,
  onSnooze,
  onOutcomes,
  onSkip,
  onIgnore,
  onOpenClient,
  onToast,
  onRefresh,
  isOwner,
}: {
  r: ReminderRow;
  tone: "red" | "amber" | "slate";
  tagText: string;
  hasDiscount: boolean;
  onSnooze: (id: string, days: number) => void;
  onOutcomes: (id: string, tags: string[], note?: string) => void;
  onSkip: (id: string) => void;
  onIgnore: (clientId: string) => void;
  onOpenClient: (id: string) => void;
  onToast: (msg: string) => void;
  onRefresh: () => void;
  isOwner: boolean;
}) {
  const [scriptIdx, setScriptIdx] = useState(0);
  // Для протермінованих rebook_1m (dueAt у минулому) — окремі теплі скрипти,
  // бо стандартний «сьогодні рівно місяць» уже не відповідає дійсності.
  const overdueRebook =
    r.kind === "rebook_1m" &&
    Date.now() - new Date(r.dueAt).getTime() >= 24 * 60 * 60 * 1000;
  const scripts = overdueRebook
    ? OVERDUE_REBOOK_SCRIPTS
    : r.scriptVariants.length > 0
      ? r.scriptVariants
      : [""];
  const [scriptText, setScriptText] = useState(scripts[0] ?? "");
  const [discount, setDiscount] = useState("");
  const [copied, setCopied] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  // «Звідки дізналась» — окрема задача з окремою відповіддю. Раніше картка
  // показувала їй ті самі «Записалась / Відгук», що й решті, хоча питання
  // зовсім про інше, а відповідь (джерело) записати було нікуди.
  // «Написала — чекаю відповіді». Локальний стан, щоб тап відгукувався
  // одразу й не перезавантажував увесь список (і не стрибав скрол).
  const [contactedAt, setContactedAt] = useState<string | null>(
    r.contactedAt ?? null,
  );
  const [contactBusy, setContactBusy] = useState(false);
  async function toggleContacted() {
    if (contactBusy) return;
    const next = !contactedAt;
    setContactBusy(true);
    try {
      await api.setReminderContacted(r.id, next);
      setContactedAt(next ? new Date().toISOString() : null);
      onToast(next ? "Позначено: написала ✓" : "Позначку знято");
    } catch (e) {
      onToast(`Не вдалось: ${String((e as Error)?.message ?? e)}`);
    } finally {
      setContactBusy(false);
    }
  }

  const isReferral = r.kind === "ask_referral_source";
  const [refSaving, setRefSaving] = useState(false);
  const [refCustom, setRefCustom] = useState(false);
  const [refText, setRefText] = useState("");
  async function saveReferral(source: string) {
    const value = source.trim();
    if (refSaving || !value) return;
    setRefSaving(true);
    try {
      await api.setReminderReferralSource(r.id, value);
      onToast("Записано ✓");
      onRefresh();
    } catch (e) {
      onToast(`Не вдалось зберегти: ${String((e as Error)?.message ?? e)}`);
    } finally {
      setRefSaving(false);
    }
  }
  const [calOpen, setCalOpen] = useState(false);
  const copyT = useRef<number | null>(null);

  const finalScript = scriptText
    .replace(/\{\{firstName\}\}/g, r.client.firstName)
    .replace(/\{\{discount\}\}/g, discount.trim() || "{{discount}}");

  const hasGood = selected.includes("review_positive");
  const needComment = selected.some((v) => NEEDS_COMMENT.has(v));
  const commentMissing = needComment && comment.trim() === "";

  const toggle = (v: string) => {
    setSelected((s) =>
      s.includes(v) ? s.filter((x) => x !== v) : [...s, v],
    );
  };
  const cycleScript = () => {
    const n = (scriptIdx + 1) % scripts.length;
    setScriptIdx(n);
    setScriptText(scripts[n] ?? "");
  };
  const copy = () => {
    try {
      void navigator.clipboard?.writeText(finalScript);
    } catch {
      /* ignore */
    }
    setCopied(true);
    onToast("Скопійовано ✓");
    if (copyT.current) window.clearTimeout(copyT.current);
    copyT.current = window.setTimeout(() => setCopied(false), 1600);
  };
  const commit = () => {
    if (commentMissing || selected.length === 0) return;
    onOutcomes(r.id, selected, comment.trim() || undefined);
  };

  // Which contact buttons to render
  const contacts: ("insta" | "viber" | "tg" | "call")[] = [];
  if (r.client.instagram) contacts.push("insta");
  if (r.client.phone) {
    contacts.push("viber");
    contacts.push("call");
  }
  if (r.client.telegramUsername) contacts.push("tg");

  const fullName = clientName(r.client);

  return (
    <div className="nzd-card" data-tone={tone}>
      {/* reopen banner (yellow coach-note from owner) */}
      {r.reopenNote && (
        <div className="nz-reopen" style={{ marginTop: 0, marginBottom: 13 }}>
          <div
            className="nz-reopen-av"
            style={{ background: avatarGradient(r.reopenedBy?.name ?? "?") }}
          >
            {(r.reopenedBy?.name ?? "?")[0]}
          </div>
          <div>
            <div className="who">
              ↩️ {r.reopenedBy?.name ?? "власниця"} ·{" "}
              {r.reopenedAt ? formatWhen(r.reopenedAt) : "нещодавно"}
            </div>
            <div className="quote">«{r.reopenNote}»</div>
          </div>
        </div>
      )}

      {/* identity row */}
      <div className="nzd-top">
        <div
          className="nzd-avatar"
          style={{ background: avatarGradient(fullName) }}
        >
          {initials(fullName)}
        </div>
        <div className="nzd-id">
          <button
            className="nzd-name"
            onClick={() => onOpenClient(r.client.id)}
          >
            {fullName}
            <span className="arr">
              <ChevronRight />
            </span>
          </button>
          <div className="nzd-meta">
            {r.kind === "rebook_1m" ? (
              <RebookOneMeta r={r} />
            ) : (
              reminderMeta(r)
            )}
          </div>
        </div>
        <span className="nzd-tag">{tagText}</span>
      </div>

      {/* contacts as round icons + inline discount */}
      <div className="nzd-contacts">
        {contacts.map((k) => {
          const m = CONTACTS_META[k];
          const href = contactHref(k, r.client);
          return (
            <a
              key={k}
              className={`nzd-cbtn ${m.cls}`}
              title={m.label}
              href={href}
              target={k === "insta" || k === "tg" ? "_blank" : undefined}
              rel="noopener"
              style={{ textDecoration: "none" }}
            >
              <span style={{ fontSize: 18 }}>{m.glyph}</span>
            </a>
          );
        })}
        <button
          className="nzd-sent"
          data-on={!!contactedAt}
          disabled={contactBusy}
          onClick={toggleContacted}
          title={
            contactedAt
              ? "Ти позначила, що написала. Тицьни ще раз, щоб зняти."
              : "Позначити, що ти написала клієнтці й чекаєш відповіді"
          }
        >
          {contactedText(contactedAt)}
        </button>
        {hasDiscount && (
          <div className="nzd-disc">
            <span>💸</span>
            <input
              className="nzd-disc-in"
              placeholder="знижка, -15%"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
          </div>
        )}
      </div>
      {hasDiscount && (
        <div className="nzd-disc-hint">Знижка підставиться у повідомлення</div>
      )}

      {/* message bubble */}
      <div className="nzd-bubble">
        <div className="nzd-bubble-head">
          <span className="l">💬 Повідомлення</span>
          {scripts.length > 1 && (
            <button onClick={cycleScript}>
              <RefreshIcon /> Інший
            </button>
          )}
        </div>
        <textarea
          value={finalScript}
          onChange={(e) => setScriptText(e.target.value)}
        />
      </div>
      <button
        className={"nz-btn " + (copied ? "copied" : "primary")}
        style={{ width: "100%", marginTop: 11, height: 46 }}
        onClick={copy}
      >
        {copied ? (
          <>
            <CheckIcon /> Скопійовано
          </>
        ) : (
          <>
            <CopyIcon /> Скопіювати повідомлення
          </>
        )}
      </button>

      {/* google review on positive selection */}
      {hasGood && (
        <div className="nz-google" style={{ marginTop: 12 }}>
          <div className="g-title">🌟 Запросити Google-відгук</div>
          <div className="g-text">
            Дякуємо за теплі слова! 💖 Якщо не складно, залиште, будь ласка,
            відгук на Google: g.page/nailzona
          </div>
        </div>
      )}

      {isReferral && (
        <>
          <div className="nzd-sectiontitle">Звідки дізналась? — один тап</div>
          <div className="nz-outcomes">
            {REFERRAL_SOURCES.map((src) => (
              <button
                key={src.key}
                className="nz-out src"
                data-c="blue"
                disabled={refSaving}
                onClick={() => saveReferral(src.key)}
              >
                <span style={{ fontSize: 13 }}>{src.emoji}</span>
                {src.label}
              </button>
            ))}
            <button
              className="nz-out wide"
              data-c="slate"
              data-on={refCustom}
              disabled={refSaving}
              onClick={() => setRefCustom(!refCustom)}
            >
              <span style={{ fontSize: 13 }}>✏️</span>
              Інше
            </button>
          </div>
          {refCustom && (
            <div className="nz-commit nz-refcustom">
              <label className="nz-field-label">✏️ Своє джерело</label>
              <input
                className="nz-textarea"
                style={{ minHeight: 0 }}
                placeholder="напр. блогерка в Threads"
                value={refText}
                maxLength={120}
                autoFocus
                onChange={(e) => setRefText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveReferral(refText);
                }}
              />
              <button
                className={
                  "nz-btn green wide" +
                  (refSaving || !refText.trim() ? " disabled" : "")
                }
                onClick={() => saveReferral(refText)}
              >
                <CheckIcon /> Зберегти джерело
              </button>
            </div>
          )}
        </>
      )}

      {/* outcome picker */}
      <div className="nzd-sectiontitle">
        {isReferral
          ? "Не вдалось дізнатись?"
          : "Як пройшло? — можна обрати декілька"}
      </div>
      <div className="nz-outcomes">
        {OUTCOMES.filter(
          (o) => !isReferral || o.v === "declined" || o.v === "not_replied",
        ).map((o) => {
          const on = selected.includes(o.v);
          return (
            <button
              key={o.v}
              className="nz-out"
              data-on={on}
              data-c={o.c}
              onClick={() => toggle(o.v)}
            >
              <span className="ck">
                <CheckIcon />
              </span>
              <span style={{ fontSize: 13 }}>{o.emoji}</span>
              {o.label}
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="nz-commit">
          <label className="nz-field-label">
            💬 Коментар{" "}
            {needComment ? "(обовʼязково для відгуку/відмови)" : "(необовʼязково)"}
          </label>
          <textarea
            className="nz-textarea"
            style={{ minHeight: 60 }}
            placeholder="Короткий коментар…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {commentMissing && (
            <div className="nz-warn">⚠️ Додай коментар, щоб зберегти</div>
          )}
          <div className="nz-script-actions">
            <button
              className={"nz-btn flex green" + (commentMissing ? " disabled" : "")}
              onClick={commit}
            >
              <CheckIcon /> Зберегти ({selected.length})
            </button>
            <button
              className="nz-btn ghost"
              onClick={() => {
                setSelected([]);
                setComment("");
              }}
            >
              Скинути
            </button>
          </div>
        </div>
      )}

      {/* snooze / skip / ignore */}
      <div className="nzd-actions">
        <span className="lbl">⏰ Відкласти нагадування</span>
        <div className="nzd-snoozeseg">
          {/* «1 тиждень», а не «тиждень»: у слові без жодної високої літери
              (цифри чи великої) візуальна маса лежить нижче, і воно виглядало
              зсунутим униз на ~3px відносно сусідів. З цифрою оптичний зсув
              стає 1.33px — рівно як у «3 дні». Плюс однакова структура
              «число + одиниця» у всіх трьох. */}
          <button onClick={() => onSnooze(r.id, 3)}>3 дні</button>
          <button onClick={() => onSnooze(r.id, 7)}>1 тиждень</button>
          <button onClick={() => onSnooze(r.id, 14)}>2 тижні</button>
          <button
            className="ico"
            title="Інша дата"
            aria-label="Обрати свою дату"
            onClick={() => setCalOpen(true)}
          >
            📅
          </button>
        </div>
        {isOwner && (
          <div className="nzd-actions2">
            <button
              className="nzd-sbtn grow"
              onClick={() => {
                if (
                  window.confirm(`Пропустити «${fullName}» цього разу?`)
                ) {
                  onSkip(r.id);
                }
              }}
            >
              ⏭ Пропустити
            </button>
            <button
              className="nzd-sbtn grow danger"
              onClick={() => {
                if (
                  window.confirm(
                    `Більше не нагадувати про «${fullName}» взагалі?`,
                  )
                ) {
                  onIgnore(r.client.id);
                }
              }}
            >
              🔕 Ігнорувати
            </button>
          </div>
        )}
      </div>
      <DateSheet
        open={calOpen}
        onClose={() => setCalOpen(false)}
        title="📅 Відкласти до"
        onPick={(days) => {
          if (days > 0) onSnooze(r.id, days);
        }}
      />
    </div>
  );
}

// ---- Inline manual-task create form ----

function ManualCreateForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<
    {
      id: string;
      firstName: string;
      lastName: string | null;
      phone: string | null;
    }[]
  >([]);
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [title, setTitle] = useState("");
  const [script, setScript] = useState("");
  const [prio, setPrio] =
    useState<"super_urgent" | "urgent" | "normal">("urgent");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (picked || q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .clients({ q: q.trim(), pageSize: 20 })
        .then((r) =>
          setResults(
            r.items as {
              id: string;
              firstName: string;
              lastName: string | null;
              phone: string | null;
            }[],
          ),
        )
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, picked]);

  async function save() {
    if (!picked || !title.trim() || busy) return;
    setBusy(true);
    try {
      await api.createManualReminder({
        clientId: picked.id,
        title: title.trim(),
        script: script.trim() || undefined,
        priority: prio,
      });
      onCreated();
    } catch (e) {
      alert(String(e));
      setBusy(false);
    }
  }

  return (
    <div className="nz-form">
      {!picked ? (
        <>
          <input
            className="nz-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="🔎 Знайти клієнтку (імʼя, телефон, @)"
            autoFocus
          />
          {results.length > 0 && (
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid var(--line-2)",
                borderRadius: 12,
              }}
            >
              {results.map((c) => (
                <button
                  key={c.id}
                  onClick={() =>
                    setPicked({
                      id: c.id,
                      name: `${c.firstName}${
                        c.lastName ? " " + c.lastName : ""
                      }`,
                    })
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 10px",
                    background: "transparent",
                    border: 0,
                    fontSize: 14,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    color: "var(--ink)",
                  }}
                >
                  {c.firstName} {c.lastName ?? ""}
                  {c.phone && (
                    <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
                      {" "}
                      · {c.phone}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            background: "var(--rose-100)",
            color: "var(--rose-700)",
            padding: "8px 12px",
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            alignSelf: "flex-start",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          👤 {picked.name}
          <button
            onClick={() => setPicked(null)}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--ink-3)",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            змінити
          </button>
        </div>
      )}
      <input
        className="nz-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Коротко що за задача"
        maxLength={120}
      />
      <textarea
        className="nz-textarea"
        style={{ minHeight: 70 }}
        value={script}
        onChange={(e) => setScript(e.target.value)}
        placeholder="Текст для клієнтки (необовʼязково)"
      />
      <div>
        <label className="nz-field-label">Пріоритет</label>
        <div className="nz-prio-row">
          <button
            className="nz-prio"
            data-c="red"
            data-on={prio === "super_urgent"}
            onClick={() => setPrio("super_urgent")}
          >
            🚨 Супер
          </button>
          <button
            className="nz-prio"
            data-c="amber"
            data-on={prio === "urgent"}
            onClick={() => setPrio("urgent")}
          >
            🔴 Термінова
          </button>
          <button
            className="nz-prio"
            data-c="slate"
            data-on={prio === "normal"}
            onClick={() => setPrio("normal")}
          >
            📋 Звичайна
          </button>
        </div>
      </div>
      <div className="nz-script-actions">
        <button
          className={
            "nz-btn flex green" +
            (!picked || !title.trim() || busy ? " disabled" : "")
          }
          onClick={save}
        >
          <CheckIcon /> Зберегти задачу
        </button>
        <button className="nz-btn ghost" onClick={onClose}>
          Скасувати
        </button>
      </div>
    </div>
  );
}

// ---- General-task form (no client) ----

function GeneralTaskForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [prio, setPrio] =
    useState<"super_urgent" | "urgent" | "normal">("normal");
  const [dueAt, setDueAt] = useState<string>("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      await api.createTask({
        title: title.trim(),
        body: body.trim() || undefined,
        priority: prio,
        dueAt: dueAt || undefined,
      });
      onCreated();
    } catch (e) {
      alert(String(e));
      setBusy(false);
    }
  }

  return (
    <div className="nz-form">
      <input
        className="nz-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Що зробити (напр. «замовити серветки»)"
        maxLength={200}
        autoFocus
      />
      <textarea
        className="nz-textarea"
        style={{ minHeight: 70 }}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Деталі (необовʼязково)"
      />
      <div>
        <label className="nz-field-label">📅 Дедлайн (необовʼязково)</label>
        <input
          className="nz-input"
          type="date"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
        />
      </div>
      <div>
        <label className="nz-field-label">Пріоритет</label>
        <div className="nz-prio-row">
          <button
            className="nz-prio"
            data-c="red"
            data-on={prio === "super_urgent"}
            onClick={() => setPrio("super_urgent")}
          >
            🚨 Супер
          </button>
          <button
            className="nz-prio"
            data-c="amber"
            data-on={prio === "urgent"}
            onClick={() => setPrio("urgent")}
          >
            🔴 Термінова
          </button>
          <button
            className="nz-prio"
            data-c="slate"
            data-on={prio === "normal"}
            onClick={() => setPrio("normal")}
          >
            📋 Звичайна
          </button>
        </div>
      </div>
      <div className="nz-script-actions">
        <button
          className={"nz-btn flex green" + (!title.trim() || busy ? " disabled" : "")}
          onClick={save}
        >
          <CheckIcon /> Зберегти задачу
        </button>
        <button className="nz-btn ghost" onClick={onClose}>
          Скасувати
        </button>
      </div>
    </div>
  );
}

// ---- Small icon stubs (kept inline so theme = single file) ----

function ChevronRight() {
  return (
    <svg
      width="9"
      height="14"
      viewBox="0 0 9 14"
      fill="none"
      style={{ display: "block" }}
    >
      <path
        d="m1.5 1 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block" }}
    >
      <path
        d="m4 12.5 5 5 11-11"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block" }}
    >
      <rect
        x="8"
        y="8"
        width="12"
        height="12"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M16 5.5A2 2 0 0 0 14 4H6a2 2 0 0 0-2 2v8a2 2 0 0 0 1.5 1.9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block" }}
    >
      <path
        d="M20 11a8 8 0 0 0-14-4.5L4 8m0-4v4h4"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 13a8 8 0 0 0 14 4.5L20 16m0 4v-4h-4"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      style={{ color: "var(--ink-3)" }}
    >
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.9" />
      <path
        d="m20 20-3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const t = new Date();
  const sameDay = d.toDateString() === t.toDateString();
  const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes(),
  ).padStart(2, "0")}`;
  if (sameDay) return `сьогодні ${hhmm}`;
  const yest = new Date(t.getTime() - 86400000);
  if (d.toDateString() === yest.toDateString()) return `вчора ${hhmm}`;
  return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")} ${hhmm}`;
}


// ============================================================
// Snoozed / Done / Stats sub-tabs (modern)
// ============================================================

export function SnoozedRemindersModern() {
  const [items, setItems] = useState<any[] | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [shown, setShown] = useState(10);
  const captureScroll = useScrollMemory(!!selectedClientId);
  const openClient = (id: string) => {
    captureScroll();
    setSelectedClientId(id);
  };

  function load() {
    api.snoozedReminders().then((d) => setItems(d as any[])).catch(() => setItems([]));
  }
  useEffect(load, []);

  if (!items) return <div className="hint" style={{ marginTop: 14 }}>Завантаження…</div>;

  const slice = items.slice(0, shown);
  // Overlay-pattern: список через display:none коли відкрита деталь,
  // зберігає скрол + shown-пагінацію при поверненні.
  return (
    <>
      <div style={{ display: selectedClientId ? "none" : "block" }}>
        {items.length === 0 ? (
          <div className="nz-empty" style={{ marginTop: 32 }}>
            <div className="em">💤</div>
            <div className="et">Відкладених нема</div>
            <div className="es">Жодне нагадування зараз не «спить».</div>
          </div>
        ) : (
          <>
            <div className="nz-summary">
              Нагадування, відкладені на потім · <b>{slice.length} з {items.length}</b>.
              Натисни «повернути», щоб одразу в активні.
            </div>
            {slice.map((r) => {
              const fullName = `${r.client.firstName} ${r.client.lastName ?? ""}`.trim();
              const due = new Date(r.dueAt);
              const dueStr = `${String(due.getDate()).padStart(2, "0")}.${String(due.getMonth() + 1).padStart(2, "0")}`;
              return (
                <div key={r.id} className="nz-sec" style={{ padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <button
                      onClick={() => openClient(r.client.id)}
                      style={{
                        minWidth: 0,
                        flex: 1,
                        textAlign: "left",
                        background: "transparent",
                        border: 0,
                        cursor: "pointer",
                        padding: 0,
                        fontFamily: "inherit",
                        color: "var(--ink)",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {fullName}
                      </div>
                      <div className="nz-row-sub" style={{ marginTop: 5 }}>
                        ⏰ Повернеться {dueStr}
                        {r.client.instagram ? ` · @${r.client.instagram}` : ""}
                      </div>
                    </button>
                    <span className="nz-chip" style={{ flexShrink: 0 }}>
                      {KIND_SHORT_MODERN[r.kind] ?? r.kind}
                    </span>
                  </div>
                  <button
                    className="nz-btn ghost"
                    style={{ width: "100%", marginTop: 11, height: 42 }}
                    onClick={async () => {
                      if (!window.confirm(`Повернути «${fullName}» в активні?`)) return;
                      await api.reopenReminder(r.id);
                      load();
                    }}
                  >
                    ↩️ Повернути в задачі
                  </button>
                </div>
              );
            })}
            {shown < items.length && (
              <button className="nz-showmore" onClick={() => setShown(shown + 10)}>
                ⬇ Показати ще ({items.length - shown})
              </button>
            )}
          </>
        )}
      </div>
      {selectedClientId && (
        <ClientDetail id={selectedClientId} onBack={() => setSelectedClientId(null)} />
      )}
    </>
  );
}

const KIND_SHORT_MODERN: Record<string, string> = {
  followup_after_visit: "🌸 Як пройшло",
  ask_referral_source: "📍 Звідки про нас",
  rebook_1m: "📅 1 міс",
  rebook_3m: "💸 3 міс",
  rebook_6m: "💸 6 міс",
  rebook_1y: "💸 1 рік+",
  negative_review_master: "⚠️ Скарга",
  manual: "📝 Ручна",
};

const OUTCOME_SHORT_MODERN: Record<string, string> = {
  booked: "✅ Записалась",
  declined: "❌ Відмовилась",
  not_replied: "🤷 Не відповіла",
  review_positive: "👍 Гарний відгук",
  review_negative: "👎 Поганий відгук",
  review_mixed: "🟡 50/50",
};

export function DoneRemindersModern() {
  const [items, setItems] = useState<any[] | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [shown, setShown] = useState(10);
  const captureScroll = useScrollMemory(!!selectedClientId);
  const openClient = (id: string) => {
    captureScroll();
    setSelectedClientId(id);
  };
  const [reopenForId, setReopenForId] = useState<string | null>(null);
  const [reopenNote, setReopenNote] = useState("");
  const [saving, setSaving] = useState(false);

  function load() {
    api.doneReminders().then((d) => setItems(d as any[])).catch(() => setItems([]));
  }
  useEffect(load, []);

  if (!items) return <div className="hint" style={{ marginTop: 14 }}>Завантаження…</div>;
  if (items.length === 0)
    return (
      <div className="nz-empty" style={{ marginTop: 32 }}>
        <div className="em">✅</div>
        <div className="et">Поки нічого не закрито</div>
      </div>
    );

  const slice = items.slice(0, shown);
  // group by date
  const groups = new Map<string, any[]>();
  for (const r of slice) {
    const d = new Date(r.actedAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }

  return (
    <>
      {selectedClientId && (
        <ClientDetail id={selectedClientId} onBack={() => setSelectedClientId(null)} />
      )}
      <div style={{ display: selectedClientId ? "none" : "block" }}>
        <div className="nz-summary">
          Закритих нагадувань: показую <b>{slice.length} з {items.length}</b>.
          Адмін про цю вкладку не знає.
        </div>
        {[...groups.entries()].map(([day, rows]) => (
          <div key={day} className="nz-daygroup">
            <div className="nz-dayhead">
              {prettyDayModern(day)} <span className="cnt">· {rows.length}</span>
            </div>
            {rows.map((r) => {
              const t = new Date(r.actedAt);
              const time = `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
              const name = `${r.client.firstName} ${r.client.lastName ?? ""}`.trim();
              const outcome =
                r.status === "ignored"
                  ? "⏭ Пропущено"
                  : r.outcome
                    ? OUTCOME_SHORT_MODERN[r.outcome] ?? r.outcome
                    : "—";
              return (
                <div key={r.id} className="nz-sec" style={{ padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <button
                      onClick={() => openClient(r.client.id)}
                      style={{
                        minWidth: 0,
                        flex: 1,
                        textAlign: "left",
                        background: "transparent",
                        border: 0,
                        cursor: "pointer",
                        padding: 0,
                        fontFamily: "inherit",
                        color: "var(--ink)",
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <span style={{ color: "var(--ink-3)", fontWeight: 700, marginRight: 7 }}>{time}</span>
                        {name}
                      </div>
                      <div className="nz-row-sub" style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 7, whiteSpace: "normal" }}>
                        → {outcome}
                        {r.closedBy?.name && <span className="nz-chip">👤 {r.closedBy.name}</span>}
                      </div>
                    </button>
                    <span className="nz-chip" style={{ flexShrink: 0 }}>
                      {KIND_SHORT_MODERN[r.kind] ?? r.kind}
                    </span>
                  </div>
                  {reopenForId === r.id ? (
                    <div style={{ marginTop: 11 }}>
                      <textarea
                        className="nz-textarea"
                        autoFocus
                        placeholder="✍️ Напиши Тані, де натупила."
                        value={reopenNote}
                        onChange={(e) => setReopenNote(e.target.value)}
                        rows={3}
                        style={{ minHeight: 60, fontSize: 13.5 }}
                      />
                      <div className="nz-script-actions">
                        <button
                          className={"nz-btn green flex" + (saving || !reopenNote.trim() ? " disabled" : "")}
                          onClick={async () => {
                            if (saving || !reopenNote.trim()) return;
                            setSaving(true);
                            try {
                              await api.reopenReminder(r.id, reopenNote.trim());
                              setReopenForId(null);
                              setReopenNote("");
                              load();
                            } finally {
                              setSaving(false);
                            }
                          }}
                        >
                          ↩️ Повернути з коментарем
                        </button>
                        <button
                          className="nz-btn ghost"
                          onClick={() => {
                            setReopenForId(null);
                            setReopenNote("");
                          }}
                        >
                          Скасувати
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="nz-btn ghost"
                      style={{ width: "100%", marginTop: 11, height: 40 }}
                      onClick={() => {
                        setReopenForId(r.id);
                        setReopenNote("");
                      }}
                    >
                      ↩️ Повернути в задачі
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        {shown < items.length && (
          <button className="nz-showmore" onClick={() => setShown(shown + 10)}>
            ⬇ Показати ще ({items.length - shown})
          </button>
        )}
      </div>
    </>
  );
}

function prettyDayModern(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today.getTime() - 86400000);
  if (d.toDateString() === today.toDateString()) return "Сьогодні";
  if (d.toDateString() === yesterday.toDateString()) return "Вчора";
  return `${["Нд", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"][d.getDay()]}, ${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ---- Stats (Reminders Statistics + Salary preview) ----

export function RemindersStatsModern() {
  const [range, setRange] = useState<PeriodValue>({ preset: "1m" });
  const [data, setData] = useState<any | null>(null);

  useEffect(() => {
    const { from, to } = periodToIsoRange(range);
    api.reminderStats(from, to).then(setData).catch(() => setData(null));
  }, [range]);

  if (!data) return <div className="hint" style={{ marginTop: 18 }}>Завантаження…</div>;

  const booked = data.byOutcome.find((o: any) => o.outcome === "booked")?.count ?? 0;
  const conv = data.total > 0 ? Math.round((booked / data.total) * 100) : 0;

  return (
    <>
      <PeriodPicker value={range} onChange={setRange} />

      <div className="nz-kpis k3">
        <div className="nz-kpi">
          <div className="l">Опрацьовано</div>
          <div className="v plum">{data.total}</div>
        </div>
        <div className="nz-kpi">
          <div className="l">Записались</div>
          <div className="v green">{booked}</div>
          <div className="s">{conv}% конв.</div>
        </div>
        <div className="nz-kpi">
          <div className="l">↩️ Повернуто</div>
          <div className="v" style={{ color: "var(--amber-ink)" }}>{data.reopened}</div>
        </div>
      </div>

      <div className="nz-sec">
        <div className="nz-sec-title">📈 Опрацьовано задач · період</div>
        {data.timeline.length >= 2 ? (
          <AreaChart
            data={data.timeline.map((t: any) => ({
              v: t.count,
              n: String(t.count),
              x: shortBucketStats(t.bucket, data.bucket),
            }))}
          />
        ) : data.timeline.length === 1 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "26px 0 18px",
            }}
          >
            <div style={{ fontSize: 38, fontWeight: 800, color: "var(--rose-700)", letterSpacing: "-1px" }}>
              {data.timeline[0].count}
            </div>
            <div className="nz-sec-sub">
              {shortBucketStats(data.timeline[0].bucket, data.bucket)}
            </div>
          </div>
        ) : (
          <div className="nz-section-hint" style={{ textAlign: "center", padding: 22 }}>
            Поки даних нема для цього періоду.
          </div>
        )}
      </div>

      {data.byOutcome.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">📋 Які відповіді клієнток</div>
          {(() => {
            const max = Math.max(...data.byOutcome.map((o: any) => o.count), 1);
            const top = [...data.byOutcome].sort((a, b) => b.count - a.count);
            return top.slice(0, 6).map((o: any, i: number) => {
              const lbl = OUTCOME_SHORT_MODERN[o.outcome] ?? o.outcome;
              return (
                <div
                  className="nz-hbar"
                  key={o.outcome}
                  title={`${lbl}: ${o.count} разів (${Math.round((o.count / max) * 100)}% від максимуму)`}
                >
                  <div className="hh">
                    <span className="lab">{lbl}</span>
                    <span className="val">{o.count}</span>
                  </div>
                  <div className="track">
                    <div
                      className="fill"
                      data-hl={i === 0 ? "true" : "false"}
                      style={{ width: Math.max(4, (o.count / max) * 100) + "%" }}
                    />
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {data.byAdmin && data.byAdmin.length > 0 && (
        <div className="nz-sec">
          <div className="nz-sec-title">👥 По адмінах</div>
          {data.byAdmin.map((a: any) => (
            <div className="nz-rate" key={a.adminId ?? "unknown"}>
              <span className="nm">👤 {a.name ?? "(невідомо)"}</span>
              <span className="th">закр. {a.count} · зап. {a.booked}</span>
              <span className="pc">{a.conversionRate}%</span>
            </div>
          ))}
        </div>
      )}

      <SalaryPreviewModern />
    </>
  );
}

function shortBucketStats(iso: string, bucket: "day" | "week" | "month"): string {
  const d = new Date(iso);
  if (bucket === "month") {
    return ["січ", "лют", "бер", "кві", "тра", "чер", "лип", "сер", "вер", "жов", "лис", "гру"][d.getMonth()] ?? "";
  }
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ---- Salary preview (admin earnings) — modern ----

function SalaryPreviewModern() {
  const [month, setMonth] = useState(currentMonth());
  const [adminId, setAdminId] = useState<string | undefined>();
  const [data, setData] = useState<any | null>(null);
  const [summary, setSummary] = useState<any | null>(null);
  const [openSec, setOpenSec] = useState<string | null>("kind");

  useEffect(() => {
    // Don't blank out the UI while fetching — replace data in-place when
    // the new payload arrives, so chip-clicks feel instant instead of flashy.
    api.adminSalary({ month, adminId }).then(setData).catch(() => {});
  }, [month, adminId]);

  useEffect(() => {
    // Summary показує ВСІХ адмінів — тільки owner може це бачити. Для
    // звичайної адмінки (data.canEdit === false) пропускаємо запит, щоб не
    // ловити 403 і не показувати їй чужі зп.
    if (data && data.canEdit === false) {
      setSummary(null);
      return;
    }
    api.adminSalarySummary({ month }).then(setSummary).catch(() => {});
  }, [month, data?.canEdit]);

  if (!data) return null;

  const pay = data.finalPay as number;

  const shiftMonth = (delta: number) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC((y as number), (m as number) - 1 + delta, 1));
    setMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  };

  return (
    <div className="nz-salhero">
      <div className="nz-salhero-top">
        <div className="nz-salhero-who">
          <div
            className="nzd-avatar"
            style={{
              background: avatarGradientStats(data.admin?.name ?? "Адмін"),
              width: 42,
              height: 42,
              borderRadius: 14,
              fontSize: 14,
            }}
          >
            {initialsStats(data.admin?.name ?? "А")}
          </div>
          <div>
            <div className="nz-salhero-name">{data.admin?.name ?? "Адмін"}</div>
            <div className="hint" style={{ fontSize: 11 }}>За {month}</div>
          </div>
        </div>
        <div className="nz-monthstep">
          <button onClick={() => shiftMonth(-1)}>‹</button>
          <span>{month}</span>
          <button onClick={() => shiftMonth(1)}>›</button>
        </div>
      </div>

      <div className="lbl" style={{ marginTop: 18 }}>Підсумок ЗП</div>
      <div className="amt">{pay.toLocaleString("uk")} ₴</div>

      <div className="nz-payout-rows">
        <div className="nz-sumrow">
          <span className="k">База</span>
          <span className="vv">{data.baseSalary.toLocaleString("uk")} ₴</span>
        </div>
        <div className="nz-sumrow">
          <span className="k">За задачами ({data.totalClosed})</span>
          <span className="vv">{data.earnedFromTasks.toLocaleString("uk")} ₴</span>
        </div>
        <div className="nz-sumrow">
          <span className="k">Конверсія в «записалась»</span>
          <span className="vv">
            {data.conversionRate}% ({data.bookedCount}/{data.totalClosed})
          </span>
        </div>
        {data.conversionBonus > 0 && (
          <div className="nz-sumrow">
            <span className="k">Бонус конверсії ≥40%</span>
            <span className="vv green-t">+{data.conversionBonus.toLocaleString("uk")} ₴</span>
          </div>
        )}
        {data.reopenedCount > 0 && (
          <div className="nz-sumrow">
            <span className="k">↩️ Повернуто на доробку × {data.reopenedCount}</span>
            <span className="vv">не оплачено</span>
          </div>
        )}
        {data.supersededCount > 0 && (
          <div className="nz-sumrow">
            <span className="k">👥 Інша адмінка переробила × {data.supersededCount}</span>
            <span className="vv">не оплачено</span>
          </div>
        )}
        {(data.tonusPenalty + (data.ownerPenalty ?? 0)) > 0 && (
          <div className="nz-sumrow">
            <span className="k">Штрафи за простій</span>
            <span className="vv red-t">
              −{(data.tonusPenalty + (data.ownerPenalty ?? 0)).toLocaleString("uk")} ₴
            </span>
          </div>
        )}
        {(data.penaltyDetails ?? []).length > 0 && (
          <div className="nz-penlist">
            {data.penaltyDetails.map((p: any, i: number) => (
              <div className="nz-penrow" key={i}>
                <div>
                  <div className="when">{salPenaltyDT(p.at)}</div>
                  <div className="why">
                    {p.kind === "warn"
                      ? "простій 8 год · штраф"
                      : "8+ год · звіт Любі"}
                    {p.idleHours != null
                      ? ` · ~${Math.round(p.idleHours)} год без задач`
                      : ""}
                  </div>
                </div>
                <span className="pen">−{p.amount.toLocaleString("uk")} ₴</span>
              </div>
            ))}
          </div>
        )}
        <div className="nz-sumrow total">
          <span className="k">Разом</span>
          <span className="vv">{pay.toLocaleString("uk")} ₴</span>
        </div>
      </div>

      <div className="nz-salbreak">
        <SalSection
          id="kind"
          title="📊 За що заробила — по типах"
          open={openSec}
          onToggle={setOpenSec}
        >
          {salBreakRows(
            (data.byKind ?? []).map((k: any) => ({
              key: k.kind,
              name: KIND_SHORT_MODERN[k.kind] ?? k.kind,
              count: `${k.count} шт`,
              earned: k.earned,
            })),
          )}
        </SalSection>

        <SalSection
          id="time"
          title="⏰ По вчасності закриття"
          open={openSec}
          onToggle={setOpenSec}
        >
          {salBreakRows(
            (data.byTimeliness ?? []).map((b: any) => ({
              key: b.label,
              name: b.label,
              sub: `×${b.weight}`,
              count: `${b.count} шт`,
              earned: b.earned,
            })),
          )}
        </SalSection>

        <SalSection
          id="daily"
          title={`📅 По днях (${(data.daily ?? []).length})`}
          open={openSec}
          onToggle={setOpenSec}
          scroll
        >
          {salBreakRows(
            (data.daily ?? []).map((d: any) => ({
              key: d.day,
              name: d.day.slice(5),
              count: `${d.count} задач`,
              earned: d.earned,
            })),
          )}
        </SalSection>
      </div>

      {summary && summary.rows.length > 1 && (
        <div className="nz-adminsel">
          {summary.rows.map((r: any) => (
            <button
              key={r.adminId}
              className="nz-adminchip"
              data-on={(adminId ?? data.admin?.id) === r.adminId}
              onClick={() => setAdminId(r.adminId)}
            >
              <span className="av" style={{ background: avatarGradientStats(r.name ?? "А") }}>
                {initialsStats(r.name ?? "А")}
              </span>
              {r.name ?? `tg:${r.telegramId}`} · {r.finalPay.toLocaleString("uk")} ₴
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SalSection({
  id,
  title,
  open,
  onToggle,
  scroll,
  children,
}: {
  id: string;
  title: string;
  open: string | null;
  onToggle: (v: string | null) => void;
  scroll?: boolean;
  children: any;
}) {
  const isOpen = open === id;
  return (
    <div className="nz-salsec" data-open={isOpen}>
      <button className="head" onClick={() => onToggle(isOpen ? null : id)}>
        <span>{title}</span>
        <span className="chev">
          <ChevronRight />
        </span>
      </button>
      {isOpen && (
        <div className={"body" + (scroll ? " scroll" : "")}>{children}</div>
      )}
    </div>
  );
}

function salBreakRows(
  rows: { key: string; name: string; sub?: string; count: string; earned: number }[],
) {
  const max = Math.max(1, ...rows.map((r) => r.earned || 0));
  return rows.map((r) => (
    <div className="nz-brk" key={r.key}>
      <span className="name">
        {r.name}
        {r.sub ? <small> {r.sub}</small> : null}
      </span>
      <span className="bar">
        <i style={{ width: `${Math.round(((r.earned || 0) / max) * 100)}%` }} />
      </span>
      <span className="cnt">{r.count}</span>
      <span className="amt2">{r.earned.toLocaleString("uk")} ₴</span>
    </div>
  ));
}

function salPenaltyDT(iso: string): string {
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: "Europe/Kyiv",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

// Дефолтний період зп = ПОТОЧНИЙ цикл 23→23, іменований за днем виплати
// (як у бекенді kyivPayCycleBounds). Сьогодні ≥23 → наступна виплата 23-го
// НАСТУПНОГО місяця (цикл ще йде); інакше — 23-го цього місяця.
function currentMonth(): string {
  const [y, m, d] = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Kyiv",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(new Date())
    .split("-")
    .map(Number);
  let py = y;
  let pm = m;
  if (d >= 23) {
    if (m === 12) {
      py = y + 1;
      pm = 1;
    } else {
      pm = m + 1;
    }
  }
  return `${py}-${String(pm).padStart(2, "0")}`;
}

function avatarGradientStats(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(150deg, hsl(${hue} 52% 70%), hsl(${(hue + 26) % 360} 50% 58%))`;
}
function initialsStats(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0] || "")[0] + ((p[1] || "")[0] || "")).toUpperCase();
}

// ---- Smooth area chart ----

function AreaChart({
  data,
  height = 158,
}: {
  data: { v: number; n: string; x: string }[];
  height?: number;
}) {
  const W = 320,
    H = height,
    padX = 5,
    padTop = 30,
    padBottom = 6;
  const max = Math.max(...data.map((d) => d.v), 1);
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const pts = data.map((d, i) => ({
    x: padX + (data.length < 2 ? innerW / 2 : (innerW * i) / (data.length - 1)),
    y: padTop + innerH * (1 - d.v / max),
    v: d.v,
    n: d.n,
  }));
  const line = nzSmooth(pts);
  const area =
    pts.length > 0
      ? `${line} L ${pts[pts.length - 1]!.x.toFixed(1)} ${H} L ${pts[0]!.x.toFixed(1)} ${H} Z`
      : "";
  let peak = 0;
  pts.forEach((p, i) => {
    if (p.v >= pts[peak]!.v) peak = i;
  });
  const pk = pts[peak];
  if (!pk) return null;
  return (
    <div className="nz-area">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height: H }}>
        <defs>
          <linearGradient id="nzAreaR" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rose-400)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--rose-400)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="var(--line-2)" strokeWidth="1" />
        <path d={area} fill="url(#nzAreaR)" />
        <path
          d={line}
          fill="none"
          stroke="var(--rose-500)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {/* invisible hit-areas with native tooltips on each data point */}
      {pts.map((p, i) => (
        <div
          key={i}
          title={`${data[i]?.x}: ${data[i]?.n} задач`}
          style={{
            position: "absolute",
            left: `${(p.x / W) * 100}%`,
            top: `${p.y}px`,
            transform: "translate(-50%, -50%)",
            width: 24,
            height: 24,
            cursor: "help",
          }}
        />
      ))}
      <div
        className="nz-area-dot"
        style={{ left: `${(pk.x / W) * 100}%`, top: `${pk.y}px` }}
      />
      <div
        className="nz-area-peak"
        title={`Пік: ${data[peak]?.x} — ${pk.n} задач`}
        style={{ left: `${(pk.x / W) * 100}%`, top: `${pk.y}px`, cursor: "help" }}
      >
        {pk.n}
      </div>
      <div className="nz-area-x">
        {data.map((d, i) => (
          <span key={i}>{d.x}</span>
        ))}
      </div>
    </div>
  );
}

function nzSmooth(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return "";
  let d = `M ${pts[0]!.x.toFixed(1)} ${pts[0]!.y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}
